import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { getAccounts, getFiscalYears, getGeneralLedger } from "../../api/accountingApi";

const GeneralLedgerPage = () => {
  const toast = useRef(null);
  const dt = useRef(null);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [data, setData] = useState([]);
  const [account, setAccount] = useState(null);
  const [closingBalance, setClosingBalance] = useState(0);
  const [filters, setFilters] = useState({ accountId: null, fiscalYearId: null, fromDate: null, toDate: null });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [accts, fys] = await Promise.all([getAccounts({ active: true }), getFiscalYears()]);
      setAccounts(accts);
      setFiscalYears(fys);
      const openFy = fys.find((f) => f.status === "open");
      if (openFy) {
        setFilters((prev) => ({ ...prev, fiscalYearId: openFy.id }));
      }
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load data" });
    } finally {
      setInitialLoading(false);
    }
  };

  const runReport = async () => {
    if (!filters.accountId) {
      toast.current?.show({ severity: "warn", summary: "Validation", detail: "Select an account" });
      return;
    }

    try {
      setLoading(true);
      const params = { accountId: filters.accountId };
      if (filters.fiscalYearId) params.fiscalYearId = filters.fiscalYearId;
      if (filters.fromDate) params.fromDate = filters.fromDate.toISOString().split("T")[0];
      if (filters.toDate) params.toDate = filters.toDate.toISOString().split("T")[0];

      const result = await getGeneralLedger(params);
      setData(result.data || []);
      setAccount(result.account || null);
      setClosingBalance(result.closing_balance || 0);
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate general ledger" });
    } finally {
      setLoading(false);
    }
  };

  const currencyTemplate = (value) => {
    if (!value || value === 0) return "-";
    return parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const balanceTemplate = (rowData) => {
    const val = parseFloat(rowData.running_balance);
    const color = val >= 0 ? "text-green-600" : "text-red-600";
    return <span className={`font-bold ${color}`}>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>;
  };

  const entryLinkTemplate = (rowData) => (
    <Button
      label={rowData.entry_number}
      className="p-button-text p-button-sm p-0"
      onClick={() => navigate(`/index/accounting/journal-entries/${rowData.entry_id || ""}`)}
    />
  );

  const accountOptions = accounts.map((a) => ({
    label: `${a.code} - ${a.name}`,
    value: a.id,
  }));

  const footerGroup = data.length > 0 ? (
    <ColumnGroup>
      <Row>
        <Column footer="Closing Balance" colSpan={4} footerStyle={{ textAlign: "right", fontWeight: "bold" }} />
        <Column footer="" />
        <Column footer="" />
        <Column
          footer={closingBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          footerStyle={{ textAlign: "right", fontWeight: "bold" }}
        />
      </Row>
    </ColumnGroup>
  ) : null;

  const leftToolbar = () => (
    <div className="flex flex-wrap gap-2 align-items-center">
      <Dropdown
        value={filters.accountId}
        options={accountOptions}
        onChange={(e) => setFilters({ ...filters, accountId: e.value })}
        placeholder="Select Account *"
        filter
        className="w-16rem"
      />
      <Dropdown
        value={filters.fiscalYearId}
        options={[{ label: "All Years", value: null }, ...fiscalYears.map((fy) => ({ label: fy.name, value: fy.id }))]}
        onChange={(e) => setFilters({ ...filters, fiscalYearId: e.value })}
        placeholder="Fiscal Year"
        className="w-12rem"
      />
      <Calendar
        value={filters.fromDate}
        onChange={(e) => setFilters({ ...filters, fromDate: e.value })}
        placeholder="From Date"
        dateFormat="yy-mm-dd"
        showIcon
        className="w-10rem"
      />
      <Calendar
        value={filters.toDate}
        onChange={(e) => setFilters({ ...filters, toDate: e.value })}
        placeholder="To Date"
        dateFormat="yy-mm-dd"
        showIcon
        className="w-10rem"
      />
      <Button label="Run" icon="pi pi-play" className="p-button-sm" onClick={runReport} loading={loading} />
    </div>
  );

  const rightToolbar = () => (
    <Button
      label="Export CSV"
      icon="pi pi-download"
      className="p-button-sm p-button-outlined"
      onClick={() => dt.current?.exportCSV()}
      disabled={data.length === 0}
    />
  );

  if (initialLoading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "60vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="mb-3">
        <h2 className="m-0">General Ledger</h2>
        <p className="text-500 mt-1 mb-0">Detailed transaction history for an account</p>
      </div>

      <Toolbar className="mb-3" left={leftToolbar} right={rightToolbar} />

      {account && (
        <div className="card p-3 mb-3 flex gap-4 align-items-center">
          <div>
            <span className="text-500">Account:</span>{" "}
            <strong>{account.code} - {account.name}</strong>
          </div>
          <div>
            <span className="text-500">Type:</span> {account.type}
          </div>
          <div>
            <span className="text-500">Normal Balance:</span> {account.normal_balance}
          </div>
        </div>
      )}

      <div className="card">
        <DataTable
          ref={dt}
          value={data}
          loading={loading}
          emptyMessage="Select an account and run the report."
          className="p-datatable-sm"
          scrollable
          scrollHeight="calc(100vh - 400px)"
          footerColumnGroup={footerGroup}
        >
          <Column field="date" header="Date" sortable style={{ minWidth: "7rem" }} />
          <Column header="Entry #" body={entryLinkTemplate} style={{ minWidth: "8rem" }} />
          <Column field="entry_description" header="Description" style={{ minWidth: "14rem" }} />
          <Column field="reference" header="Reference" style={{ minWidth: "7rem" }} />
          <Column
            field="debit"
            header="Debit"
            body={(row) => currencyTemplate(row.debit)}
            style={{ minWidth: "7rem", textAlign: "right" }}
          />
          <Column
            field="credit"
            header="Credit"
            body={(row) => currencyTemplate(row.credit)}
            style={{ minWidth: "7rem", textAlign: "right" }}
          />
          <Column
            header="Balance"
            body={balanceTemplate}
            style={{ minWidth: "8rem", textAlign: "right" }}
          />
        </DataTable>
      </div>

      <Toast ref={toast} />
    </div>
  );
};

export default GeneralLedgerPage;
